'use client';

import { c as _c } from "react/compiler-runtime";
import { Popover as PopoverPrimitive } from 'radix-ui';
import type * as React from 'react';
import type { ReactNode } from 'react';
import { Popover, PopoverAnchor, PopoverHeader, PopoverTitle, PopoverTrigger, PopoverContent as ShadcnPopoverContent, PopoverDescription as ShadcnPopoverDescription } from '@/components/ui/popover';
import { cn } from '@/lib/ui/utils';

// Re-export compound primitives — see app-dialog.tsx for migration rationale.
export { Popover, PopoverAnchor, PopoverHeader, PopoverTitle, PopoverTrigger };

// Morfi overlay surface + z-index (merged over stock z-50 / rounded-md / border / bg-surface-floating / p-4).
const POPOVER_CONTENT_MORFI_TOKENS = 'z-popover overflow-hidden rounded-overlay border border-border-primary bg-surface-floating shadow-elevation-popover will-change-transform' as const;
const POPOVER_DESCRIPTION_TYPOGRAPHY = 'text-body' as const;
type PopoverContentProps = React.ComponentProps<typeof ShadcnPopoverContent>;
type PopoverDescriptionProps = React.ComponentProps<typeof ShadcnPopoverDescription>;
export function PopoverContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(POPOVER_CONTENT_MORFI_TOKENS, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnPopoverContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function PopoverDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(POPOVER_DESCRIPTION_TYPOGRAPHY, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnPopoverDescription className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export type AppPopoverSide = 'top' | 'right' | 'bottom' | 'left';
export type AppPopoverAlign = 'start' | 'center' | 'end';
export interface AppPopoverProps {
  trigger: ReactNode;
  content: ReactNode;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** Whether the popover traps focus / uses a focus-blocking overlay. Defaults to `false` so hover-to-open patterns (see `frontend/.claude/rules/hover-dropdown-pattern.md`) work without flicker. */
  modal?: boolean | undefined;
  side?: AppPopoverSide | undefined;
  align?: AppPopoverAlign | undefined;
  sideOffset?: number | undefined;
  alignOffset?: number | undefined;
  contentClassName?: string | undefined;
}
interface AppPopoverComponent {
  (props: AppPopoverProps): React.ReactNode;
  Trigger: typeof PopoverTrigger;
  Content: typeof PopoverContent;
}
function AppPopoverImpl(t0) {
  const $ = _c(29);
  const {
    trigger,
    content,
    open,
    onOpenChange,
    modal: t1,
    side,
    align,
    sideOffset,
    alignOffset,
    contentClassName
  } = t0;
  const modal = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== open) {
    t2 = open !== undefined ? {
      open
    } : {};
    $[0] = open;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onOpenChange) {
    t3 = onOpenChange !== undefined ? {
      onOpenChange
    } : {};
    $[2] = onOpenChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== trigger) {
    t4 = <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>;
    $[4] = trigger;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== side) {
    t5 = side !== undefined ? {
      side
    } : {};
    $[6] = side;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== align) {
    t6 = align !== undefined ? {
      align
    } : {};
    $[8] = align;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== sideOffset) {
    t7 = sideOffset !== undefined ? {
      sideOffset
    } : {};
    $[10] = sideOffset;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== alignOffset) {
    t8 = alignOffset !== undefined ? {
      alignOffset
    } : {};
    $[12] = alignOffset;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== contentClassName) {
    t9 = cn(contentClassName);
    $[14] = contentClassName;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== content || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <PopoverContent {...t5} {...t6} {...t7} {...t8} className={t9}>{content}</PopoverContent>;
    $[16] = content;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== modal || $[24] !== t10 || $[25] !== t2 || $[26] !== t3 || $[27] !== t4) {
    t11 = <Popover {...t2} {...t3} modal={modal}>{t4}{t10}</Popover>;
    $[23] = modal;
    $[24] = t10;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}
export const AppPopover: AppPopoverComponent = Object.assign(AppPopoverImpl, {
  Trigger: PopoverTrigger,
  Content: PopoverContent
});

// Re-export the Radix anchor for callsites that need it (rare).
export const AppPopoverAnchor: typeof PopoverPrimitive.Anchor = PopoverPrimitive.Anchor;